import React from 'react';
import groceryImage from '../images/Consultantimg.jpg';
import 'bootstrap/dist/css/bootstrap.min.css';
import { MdCheckCircle, MdLocalShipping, MdAttachMoney, MdStars, MdKitchen } from 'react-icons/md';
import { motion } from 'framer-motion';

const ProductInquiryPage = () => {
  const ORANGE = '#ff6701';
  const GREEN = '#4caf50';
  const DARK = '#232f3e';

  const styles = {
    section: {
      padding: '80px 0',
      backgroundColor: '#fff',
      fontFamily: "'Rubik', sans-serif",
      overflow: 'hidden',
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
    },
    row: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '40px',
    },
    imageCol: {
      flex: '1 1 450px',
      position: 'relative',
    },
    imageWrapper: {
      borderRadius: '30px',
      overflow: 'hidden',
      boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
      border: '8px solid #fff',
    },
    image: {
      width: '100%',
      height: '500px',
      objectFit: 'cover',
    },
    contentCol: {
      flex: '1 1 450px',
    },
    badge: {
      display: 'inline-block',
      backgroundColor: `${GREEN}15`,
      color: GREEN,
      padding: '8px 16px',
      borderRadius: '30px',
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '20px',
    },
    title: {
      fontSize: '42px',
      fontWeight: '800',
      color: DARK,
      marginBottom: '24px',
      lineHeight: 1.2,
    },
    highlight: {
      color: GREEN,
    },
    description: {
      fontSize: '18px',
      color: '#666',
      lineHeight: 1.6,
      marginBottom: '32px',
    },
    featureList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px',
      marginBottom: '40px',
    },
    featureItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    },
    iconWrapper: {
      backgroundColor: `${GREEN}10`,
      color: GREEN,
      padding: '10px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
    },
    featureText: {
      fontSize: '15px',
      fontWeight: '600',
      color: DARK,
      margin: 0,
    },
    featureSub: {
      fontSize: '13px',
      color: '#888',
      margin: 0,
    },
    benefitsRow: {
      display: 'flex',
      gap: '24px',
      flexWrap: 'wrap',
    },
    benefitCard: {
      flex: '1 1 140px',
      padding: '20px',
      borderRadius: '20px',
      backgroundColor: '#f8f9fa',
      textAlign: 'center',
      transition: 'all 0.3s ease',
    },
    benefitIcon: {
      fontSize: '28px',
      marginBottom: '10px',
    }
  };

  return (
    <section style={styles.section}>
      <div style={styles.container}>
        <div style={styles.row}>
          {/* Image Section */}
          <motion.div 
            style={styles.imageCol}
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div style={styles.imageWrapper}>
              <img
                src={groceryImage}
                alt="Shakambari Groceries"
                style={styles.image}
              />
            </div>
            {/* Floating Experience Badge */}
            <div style={{
              position: 'absolute',
              bottom: '40px',
              right: '-20px',
              backgroundColor: ORANGE,
              color: '#fff',
              padding: '20px',
              borderRadius: '20px',
              boxShadow: '0 10px 30px rgba(255,103,1,0.3)',
              textAlign: 'center'
            }}>
              <h4 style={{ margin: 0, fontSize: '24px', fontWeight: '800' }}>10+</h4>
              <p style={{ margin: 0, fontSize: '12px', fontWeight: '600' }}>Years of Trust</p>
            </div>
          </motion.div>

          {/* Content Section */}
          <motion.div 
            style={styles.contentCol}
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span style={styles.badge}>Our Commitment</span>
            <h1 style={styles.title}>
              Freshness <span style={styles.highlight}>Delivered</span> Directly To Your Kitchen
            </h1>
            <p style={styles.description}>
              Welcome to <strong>Shakambari Groceries</strong> – your one-stop destination for premium quality daily essentials, sourced with love and delivered with care.
            </p>

            <div style={styles.featureList}>
              <div style={styles.featureItem}>
                <div style={styles.iconWrapper}><MdKitchen /></div>
                <div>
                  <p style={styles.featureText}>Fresh Picked</p>
                  <p style={styles.featureSub}>Daily farm sourcing</p>
                </div>
              </div>
              <div style={styles.featureItem}>
                <div style={styles.iconWrapper}><MdStars /></div>
                <div>
                  <p style={styles.featureText}>Organic Staples</p>
                  <p style={styles.featureSub}>100% pure quality</p>
                </div>
              </div>
            </div>

            <h5 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: DARK }}>Why Choose Us?</h5>
            <div style={styles.benefitsRow}>
              {[
                { icon: <MdAttachMoney />, label: 'Best Prices', color: '#3b82f6' },
                { icon: <MdCheckCircle />, label: 'Trusted Quality', color: '#10b981' },
                { icon: <MdLocalShipping />, label: 'Fast Delivery', color: ORANGE },
              ].map((item, i) => (
                <motion.div 
                  key={i} 
                  style={styles.benefitCard}
                  whileHover={{ y: -5, backgroundColor: '#fff', boxShadow: '0 10px 20px rgba(0,0,0,0.05)' }}
                >
                  <div style={{ ...styles.benefitIcon, color: item.color }}>{item.icon}</div>
                  <p style={{ ...styles.featureText, fontSize: '13px' }}>{item.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProductInquiryPage;

